import { appState } from '../state';
import { monthLabel, shiftMonth } from '../format';
import { listMonths, ensureMonth, metaSet, resetMonthPaid } from '@/storage/repo';
import { toggleTheme } from '../theme';
import { icon, refreshIcons } from '../icons';
import { fullFactoryReset } from '@/storage/migrateFromV1';
import { recalcAndRender } from '../render';

export function renderHeader(root: HTMLElement) {
  const s = appState.get();
  root.innerHTML = `
    <header class="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800" style="backdrop-filter: blur(8px)">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <a href="#/home" class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-emerald-500 flex items-center justify-center shadow-md text-white">
            ${icon('wallet', 'w-5 h-5')}
          </div>
          <div>
            <h1 class="text-lg sm:text-xl font-semibold tracking-tight">Budget Tracker</h1>
            <p class="text-xs text-slate-500 dark:text-slate-400">v2 · Local-first · ZAR</p>
          </div>
        </a>
        <div class="flex flex-wrap items-center gap-2">
          <div class="relative">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">${icon('calendar', 'w-4 h-4')}</span>
            <select id="monthSelect" class="pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"></select>
          </div>
          <button id="addMonthBtn" title="Add next month" class="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition">
            ${icon('plus-circle', 'w-4 h-4')}<span class="hidden sm:inline">New Month</span>
          </button>
          <button id="hideBtn" title="Hide / show amounts" class="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition">
            ${s.amountsHidden ? icon('eye-off', 'w-4 h-4') : icon('eye', 'w-4 h-4')}
          </button>
          <button id="themeBtn" title="Toggle theme" class="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition">
            ${s.theme === 'dark' ? icon('sun', 'w-4 h-4') : icon('moon', 'w-4 h-4')}
          </button>
          <a href="#/upload" class="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-indigo-200 text-indigo-700 dark:border-indigo-700/50 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition">
            ${icon('upload', 'w-4 h-4')}<span class="hidden sm:inline">Import</span>
          </a>
          <button id="resetMonthBtn" title="Uncheck all items" class="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-amber-200 text-amber-700 dark:border-amber-700/50 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition">
            ${icon('rotate-ccw', 'w-4 h-4')}<span class="hidden sm:inline">Reset Month</span>
          </button>
          <button id="factoryResetBtn" title="Factory reset" class="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-rose-200 text-rose-700 dark:border-rose-700/50 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition">
            ${icon('trash-2', 'w-4 h-4')}<span class="hidden sm:inline">Factory</span>
          </button>
        </div>
      </div>
    </header>
  `;
  refreshIcons();

  populateMonthSelect();

  document.getElementById('monthSelect')?.addEventListener('change', async (e) => {
    const ym = (e.target as HTMLSelectElement).value;
    await ensureMonth(ym, monthLabel(ym));
    appState.set(s => ({ ...s, currentMonth: ym }));
    await metaSet('currentMonth', ym);
    recalcAndRender();
  });
  document.getElementById('addMonthBtn')?.addEventListener('click', async () => {
    const next = shiftMonth(appState.get().currentMonth, 1);
    await ensureMonth(next, monthLabel(next));
    appState.set(s => ({ ...s, currentMonth: next }));
    await metaSet('currentMonth', next);
    await populateMonthSelect();
    recalcAndRender();
  });
  document.getElementById('hideBtn')?.addEventListener('click', async () => {
    const next = !appState.get().amountsHidden;
    appState.set(s => ({ ...s, amountsHidden: next }));
    await metaSet('amountsHidden', next ? '1' : '0');
    recalcAndRender();
  });
  document.getElementById('themeBtn')?.addEventListener('click', toggleTheme);
  document.getElementById('resetMonthBtn')?.addEventListener('click', async () => {
    if (!confirm(`Uncheck all items for ${monthLabel(appState.get().currentMonth)}?`)) return;
    await resetMonthPaid(appState.get().currentMonth);
    recalcAndRender();
  });
  document.getElementById('factoryResetBtn')?.addEventListener('click', async () => {
    if (!confirm('Restore factory defaults? This wipes ALL months, expenses and imported transactions.')) return;
    await fullFactoryReset();
    location.reload();
  });
}

async function populateMonthSelect() {
  const sel = document.getElementById('monthSelect') as HTMLSelectElement | null;
  if (!sel) return;
  const stored = await listMonths();
  const ymSet = new Set(stored.map(m => m.ym));
  for (let i = 0; i <= 12; i++) ymSet.add(shiftMonth('2026-03', i));
  const all = Array.from(ymSet).sort();
  sel.innerHTML = all
    .map(m => `<option value="${m}" ${m === appState.get().currentMonth ? 'selected' : ''}>${monthLabel(m)}</option>`)
    .join('');
}
